/**
 * A metadata value that can be copied to clipboard.
 * Compare with frontends/app/src/components/CopyableText.tsx
 */

import {CopyButton, Link} from '@wandb/components';
import isUrl from 'is-url';
import React, {type ReactNode, useCallback} from 'react';

import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {Analytics} from '../../services/analytics';
import {useViewer} from '../../state/viewer/hooks';
import {formatNumber} from '../util/formatNumber';

type CopyableTextProps = {
  toastText?: string;
  rootLocation: string;
  children: ReactNode;
  copyText?: string;
};

export const CopyableText = ({
  children,
  copyText,
  rootLocation,
}: CopyableTextProps) => {
  const {entityName, projectName} = useProjectParams();
  const viewer = useViewer();

  const onCopy = useCallback(() => {
    Analytics.track(`copy to clipboard from run overview`, {
      entityName,
      projectName,
      viewerID: viewer?.id ?? '',
      location: rootLocation,
    });
  }, [entityName, projectName, rootLocation, viewer?.id]);

  let content = children;
  let copyContent = copyText;
  if (typeof children === 'string' && isUrl(children)) {
    copyContent = children;
    content = (
      <Link target="_blank" href={children}>
        {children}
      </Link>
    );
  } else if (typeof children === 'number') {
    copyContent = String(children);
    content = formatNumber(children);
  } else if (children === null) {
    copyContent = 'null';
    content = 'null';
  } else if (typeof children === 'boolean') {
    copyContent = String(children);
    content = String(children);
  }

  return (
    <div className="group inline-flex flex-row gap-4 items-center  hover:bg-moon-100 dark:hover:bg-moon-800 focus-within:bg-moon-100 dark:focus-within:bg-moon-800 rounded px-4">
      <div>{content}</div>
      {copyContent !== undefined ? (
        <div className="transition-opacity opacity-0 group-focus-within:opacity-100 group-hover:opacity-100">
          <CopyButton content={copyContent} size="sm" onCopy={onCopy} />
        </div>
      ) : null}
    </div>
  );
};

export default CopyableText;
